import { Field, Form } from 'react-final-form';
import { Link } from 'react-router-dom';
import { BusinessRegistrationStatus } from '../../../mappers/users/toUserModel';
import { useExperimentFlag } from '../../../utils/useExperimentFlag';
import { useTranslation } from '../../../utils/useTranslation';
import { Button } from '../../input/Button';
import { Checkbox } from '../../input/Checkbox';
import { FormControlLabel } from '../../input/FormControlLabel';
import { Box } from '../../layout/Box';
import { Flex } from '../../layout/Flex';
import { Typography } from '../../typography/Typography';
import { CpaTrialDisclaimer } from '../../users/CpaTrialDisclaimer/CpaTrialDisclaimer';
import { useCpaTrial } from '../../users/CpaTrialDisclaimer/hooks/useCpaTrial';
import { GeoSuggestField } from '../GeoSuggestField/GeoSuggestField';
import { BusinessType } from '../types';
import { textDictionary, validateOfferAgreement } from './constants';
import { BloggerFields } from './parts/BloggerFields';
import { BusinessFields } from './parts/BusinessFields';
import { BusinessRegistrationFormProps } from './types';

export const BusinessRegistrationForm = ({
    businessType,
    initialValues,
    geo,
    registrationStatus,
    onSubmit,
    onFieldBlur,
    onChangeTypeClick,
}: BusinessRegistrationFormProps) => {
    const t = useTranslation();
    const isBlogger = businessType === BusinessType.BLOGGER;
    const { isNotRegistered } = useCpaTrial();

    const textMap = textDictionary[businessType];

    const enableServiceRegistration =
        Number(useExperimentFlag('enable_service_registration')) === 1;

    const isDeclined = registrationStatus === BusinessRegistrationStatus.DECLINED;

    return (
        <Form
            onSubmit={onSubmit}
            initialValues={initialValues}
            render={({ submitting, pristine, invalid, submitError, handleSubmit }) => (
                <Flex
                    as="form"
                    width="100%"
                    height={{ xs: '100%', sm: 'auto' }}
                    onSubmit={handleSubmit}
                    spacing={1}
                >
                    <Flex
                        px={2}
                        py={enableServiceRegistration ? 2 : 0}
                        width="100%"
                        height={{ xs: '100%', sm: 'auto' }}
                        borderRadius={2}
                        borderTopLeftRadius={
                            enableServiceRegistration && !isDeclined ? { xs: 0, sm: 2 } : undefined
                        }
                        borderTopRightRadius={
                            enableServiceRegistration && !isDeclined ? { xs: 0, sm: 2 } : undefined
                        }
                        backgroundColor="var(--background-primary)"
                    >
                        {isBlogger ? (
                            <BloggerFields
                                onFieldBlur={onFieldBlur}
                                onChangeTypeClick={onChangeTypeClick}
                            />
                        ) : (
                            <BusinessFields
                                isService={businessType === BusinessType.SERVICE}
                                onFieldBlur={onFieldBlur}
                                onChangeTypeClick={onChangeTypeClick}
                            />
                        )}
                        {geo ? <GeoSuggestField {...geo} mt={2} px={0} required /> : null}
                        <Field
                            type="checkbox"
                            name="offerAgreement"
                            validate={validateOfferAgreement}
                            render={({ input }) => (
                                <FormControlLabel
                                    pt={2}
                                    pb={1}
                                    label={
                                        <Typography>
                                            {t('Я принимаю условия ')}
                                            <Typography
                                                display="inline"
                                                as={Link}
                                                to={process.env.REACT_APP_TERM_OF_USE || ''}
                                                color="link"
                                                textDecoration="none"
                                                target="_blank"
                                            >
                                                {t('пользовательского соглашения')}
                                            </Typography>
                                        </Typography>
                                    }
                                    labelVerticalPadding={0}
                                    control={
                                        <Checkbox
                                            {...input}
                                            onChange={(event) => {
                                                input.onChange(event);
                                                onFieldBlur(
                                                    'offerAgreement',
                                                    event.currentTarget.checked ? 'checked' : '',
                                                );
                                            }}
                                        />
                                    }
                                />
                            )}
                        />
                        <Field
                            type="checkbox"
                            name="sendAdMails"
                            render={({ input }) => (
                                <FormControlLabel
                                    pt={1}
                                    pb={2}
                                    label={t('Я согласен получать письма от Ритма')}
                                    labelVerticalPadding={0}
                                    control={
                                        <Checkbox
                                            {...input}
                                            onChange={(event) => {
                                                input.onChange(event);
                                                onFieldBlur(
                                                    'sendAdMails',
                                                    event.currentTarget.checked ? 'checked' : '',
                                                );
                                            }}
                                        />
                                    }
                                />
                            )}
                        />
                        <Typography variant="caption" color="secondary" pb={2}>
                            {t(
                                '¹Meta, владеющая Instagram, признана экстремистской организацией на территории РФ',
                            )}
                        </Typography>

                        <Flex width="100%" pb={2} pt={1}>
                            <Box mt="auto" width="100%">
                                {submitError ? (
                                    <Typography variant="caption" color="error" mb={2}>
                                        {submitError}
                                        <br />
                                    </Typography>
                                ) : null}
                                <Button
                                    width="100%"
                                    size="l"
                                    type="submit"
                                    disabled={
                                        pristine ||
                                        submitting ||
                                        invalid ||
                                        (geo && !Object.keys(geo.selected).length)
                                    }
                                >
                                    {t(textMap.submitButtonText)}
                                </Button>
                                {isBlogger && isNotRegistered ? <CpaTrialDisclaimer /> : null}
                            </Box>
                        </Flex>
                    </Flex>
                </Flex>
            )}
        />
    );
};
